import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Station } from '@mi-front/core';
import { LinkItem } from '@mi-front/ui';
import { defineMessage, useIntl } from 'react-intl';
import { useIsContentAiForStationEnabled } from '../../../../content-ai/hooks/useIsContentAiForStationEnabled';
import { useFragment } from '../hooks/useFragment';

const mediaNotReadyTooltip = defineMessage({
  id: 'fragments.viewScreen.mediaNotReadyTooltip',
  defaultMessage: 'Esperando por media de fragmento',
});

const transcriptionNotReadyTooltip = defineMessage({
  id: 'fragments.viewScreen.transcriptionNotReadyTooltip',
  defaultMessage: 'Esperando por transcripción de fragmento',
});

/**
 * Returns the tab items for the fragment view header (Info, Archivos, Edición, Publicar, etc.).
 * Tabs are filtered by station type and feature flags (e.g. video edition, Content AI).
 * Expects {@link useFragmentCtx} to be available (fragment view page).
 */
export function useFragmentTabs(station: Station): LinkItem[] {
  const { fragment } = useFragment();
  const { $t } = useIntl();

  const isVideoEditionEnabled = useFeatureIsOn('app__fragments_video_edition_enabled');
  const isContentAiEnabled = useIsContentAiForStationEnabled(station.id);
  const isMediaReady = !!fragment.mediaCompletedAt;
  const isTranscriptionReady = fragment.transcriptionCompletedAt !== null;

  const tabs: (LinkItem | null)[] = [
    {
      exact: true,
      to: `/fragments/${fragment.id}`,
      text: $t({
        id: 'fragments.viewScreen.title',
        defaultMessage: 'Info',
      }),
      isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}`,
    },
    {
      to: `/fragments/${fragment.id}/files`,
      text: $t({
        id: 'fragments.exportView.tabFiles',
        defaultMessage: 'Archivos',
      }),
      isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}/files`,
      disabled: !isMediaReady,
      tooltip: !isMediaReady ? $t(mediaNotReadyTooltip) : undefined,
    },
    isVideoEditionEnabled
      ? {
          to: `/fragments/${fragment.id}/video-editions`,
          text: $t({
            id: 'fragments.videoEditions.tabTitle',
            defaultMessage: 'Edición',
          }),
          disabled: !isTranscriptionReady,
          tooltip: !isTranscriptionReady ? $t(transcriptionNotReadyTooltip) : undefined,
        }
      : null,
    {
      to: `/fragments/${fragment.id}/publish`,
      text: $t({
        id: 'fragments.exportView.tabPublish',
        defaultMessage: 'Publicar',
      }),
      isActive: (_match, location) => location.pathname === `/fragments/${fragment.id}/publish`,
      disabled: !isMediaReady,
      tooltip: !isMediaReady ? $t(mediaNotReadyTooltip) : undefined,
    },
    {
      to: `/fragments/${fragment.id}/audiogram`,
      text: $t({
        id: 'fragments.audiogram.title',
        defaultMessage: 'Audiogram',
      }),
      disabled: !isTranscriptionReady,
      tooltip: !isTranscriptionReady ? $t(transcriptionNotReadyTooltip) : undefined,
    },
    isContentAiEnabled
      ? ({
          to: `/fragments/${fragment.id}/content-ai`,
          text: $t({
            id: 'contentAi.title',
            defaultMessage: 'Content AI',
          }),
          disabled: !isTranscriptionReady,
          tooltip: !isTranscriptionReady ? $t(transcriptionNotReadyTooltip) : undefined,
        } as LinkItem)
      : null,
  ];

  return tabs.filter((v): v is LinkItem => Boolean(v));
}
